// START IMPORTS
// Refresh lib imports
import {
  AppConfig,
  AppConstants,
  ReportContextProvider,
} from '@ltvco/refresh-lib/ctx';
import { Grid, Box, styled } from '@ltvco/refresh-lib/theme';
import {
  ReportLoading,
  reportGetters,
  SearchReportResponseData,
  useSession,
  SearchFormProps,
} from '@ltvco/refresh-lib/v1';
import { SearchFooter } from '@ltvco/refresh-lib/v2';
import { useMutation, useQueryClient } from '@ltvco/refresh-lib/vendors';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

// Internal - components
import {
  EmailReport,
  PhoneReport,
  SocialReport,
  ContactReportV2,
  PhoneFraudReport,
  EmailFraudReport,
  IpFraudReport,
  UrlFraudReport,
} from 'components/reports';
import { Sidenav } from 'components/sidenav/Sidenav';
// END IMPORTS

// START HELPER_FUNCTIONS
function useQueryParams(params: string) {
  return useMemo(() => new URLSearchParams(params), [params]);
}
// END HELPER_FUNCTIONS

// START STYLED_COMPONENTS
const ReportBody = styled('div')(({ theme }) => ({
  width: '100%',
  margin: 'auto',
  maxWidth: '1600px',

  [theme.breakpoints.up('md')]: {
    width: '85%',
    margin: 'auto',
  },

  [theme.breakpoints.up('lg')]: {
    width: '75%',
    margin: 'auto',
  },
}));

const FooterSearchContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(4),

  // Not only the ReportBody, but also the AppContent (App.tsx) are limited to 1600px max width
  // So with this, we make sure the footer search is always 100% of the screen width
  [theme.breakpoints.up(1600)]: {
    width: '100vw',
    marginLeft: 'calc(-100vw / 2 + 1600px / 2)',
    marginRight: 'calc(-100vw / 2 + 1600px / 2)',
  },
}));
// END STYLED_COMPONENTS

// START INTERFACES_AND_TYPES
interface CommonReportProps {
  onSearchSuccess?: SearchFormProps['onSuccess'];
  onSearchError?: SearchFormProps['onError'];
}
// END INTERFACES_AND_TYPES

export function Report({ onSearchSuccess, onSearchError }: CommonReportProps) {
  // START HOOKS_AND_QUERIES
  const { search } = useLocation();
  const { reportType } = useParams();
  const { logError } = useContext(AppConfig);
  const {
    featureFlags: { propertyReportFlags },
  } = useContext(AppConstants);
  const queryClient = useQueryClient();
  const queryParams = useQueryParams(search);
  const { session, setSession, useAccount } = useSession();
  const { refetch: refetchAccount } = useAccount(session.isAuthenticated);
  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const permalinkQuery = queryParams.get('permalink') || '';
  const [permalink, setPermalink] = useState(permalinkQuery);
  const [isMonitored] = useState(false);
  // END STATE_MANAGEMENT

  // START DERIVED_DATA_AND_UTILS

  const getReportGetter = (type: string | undefined) => {
    if (!type || !(type in reportGetters)) {
      throw new Error(`Invalid report type: ${type}`);
    }
    return reportGetters[type as keyof typeof reportGetters];
  };

  const reportGetter = getReportGetter(reportType);
  const loading = !permalink;

  if (reportType === 'property') {
    // Append report flags before generating the new report
    queryParams.set('report_flags', JSON.stringify(propertyReportFlags));
  }

  const permalinkMutation = useMutation({
    mutationFn: () => reportGetter({ queryParams }),
    onSuccess: async (data: SearchReportResponseData) => {
      const { data: accountData } = await refetchAccount();
      setSession({
        account: accountData,
      });
      setPermalink(data.report.permalink);
    },
    onError: (error: Error, variables, context) => {
      logError('Report Route', error);

      const defaultOptions = queryClient.getDefaultOptions();

      // if mutation/query onError is overridden, we must still call the
      // global handler to make sure backend error codes are handled properly
      if (defaultOptions.mutations && defaultOptions.mutations.onError) {
        defaultOptions.mutations.onError(error, variables, context);
      }
    },
  });
  // END DERIVED_DATA_AND_UTILS

  // START EFFECTS
  useEffect(() => {
    if (
      !permalink &&
      !permalinkMutation.isError &&
      !permalinkMutation.isLoading
    ) {
      permalinkMutation.mutate();
    } else if (permalinkQuery && permalink !== permalinkQuery) {
      setPermalink(permalinkQuery);
    }
  }, [permalink, permalinkMutation, permalinkQuery]);
  // END EFFECTS

  // START RENDER
  return (
    <ReportContextProvider permalink={permalink} reportType={reportType || ''}>
      {loading ? (
        <ReportLoading menuItems={10} />
      ) : (
        <Grid sx={{ bgcolor: 'background.default' }} container>
          <Sidenav onReport={true} />
          <ReportBody>
            {reportType === 'phone' && (
              <PhoneReport permalink={permalink} isMonitored={isMonitored} />
            )}
            {reportType === 'email' && (
              <EmailReport permalink={permalink} isMonitored={isMonitored} />
            )}
            {reportType === 'username' && (
              <SocialReport permalink={permalink} />
            )}
            {reportType === 'contact' && (
              <ContactReportV2 permalink={permalink} />
            )}
            {reportType === 'phone-fraud' && (
              <PhoneFraudReport permalink={permalink} />
            )}
            {reportType === 'email-fraud' && (
              <EmailFraudReport
                onSearchSuccess={onSearchSuccess}
                onSearchError={onSearchError}
                permalink={permalink}
              />
            )}

            {reportType === 'ip-fraud' && (
              <IpFraudReport
                onSearchSuccess={onSearchSuccess}
                onSearchError={onSearchError}
                permalink={permalink}
              />
            )}
            {reportType === 'url-fraud' && (
              <UrlFraudReport
                onSearchSuccess={onSearchSuccess}
                onSearchError={onSearchError}
                permalink={permalink}
              />
            )}
          </ReportBody>
        </Grid>
      )}

      <FooterSearchContainer>
        <SearchFooter
          onSearchSuccess={onSearchSuccess}
          onSearchError={onSearchError}
        />
      </FooterSearchContainer>
    </ReportContextProvider>
  );
  // END RENDER
}
